import { faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useAccessibility } from '../../../../Store/contexts/AccessibilityContext';
import { TranscricaoListaItem } from '../../../../interfaces/entities/Transcricao/TranscricaoListaItem';
import TranscricaoSituacaoBadge from './TranscricaoSituacaoBadge';
import {
  formatTranscricaoDataHora,
  getTranscricaoDataSolicitacao,
  getTranscricaoItemId,
  getTranscricaoProtocolo,
  getTranscricaoStatusFromItem,
} from '../utils/transcricaoListaUtils';
import { MOBILE_BREAKPOINT } from '../transcricaoAnexoUtils';
import TranscricaoListaCardList from './TranscricaoListaCardList';
import { sxTranscricaoListaTable } from './sxProps';

interface Props {
  itens: TranscricaoListaItem[];
  onVerDetalhes: (item: TranscricaoListaItem) => void;
}

const COLUNAS = ['Protocolo', 'Data de solicitação', 'Situação', 'Detalhes'] as const;
const COLUNA_WIDTHS = ['32%', '24%', '24%', '20%'] as const;

const TranscricaoListaTable: React.FC<Props> = ({ itens, onVerDetalhes }) => {
  const accessibility = useAccessibility();
  const isMobileLayout = useMediaQuery(MOBILE_BREAKPOINT);

  if (isMobileLayout) {
    return <TranscricaoListaCardList itens={itens} onVerDetalhes={onVerDetalhes} />;
  }

  const StyledTableCell = styled(TableCell)(() => ({
    fontFamily: 'Unimed-Sans',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    border: 'none',
    verticalAlign: 'middle',
    ...accessibility.textStyleAccessibility,
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#F9F9F9',
    },
  }));

  return (
    <TableContainer
      className="transcricao-lista-table-container"
      sx={sxTranscricaoListaTable}
      style={accessibility.contrastBorderAccessibility}
      component={Paper}
    >
      <Table
        stickyHeader
        size="small"
        aria-label="Minhas transcrições"
        className="transcricao-lista-table"
      >
        <TableHead>
          <TableRow>
            {COLUNAS.map((coluna, index) => (
              <StyledTableCell key={coluna} sx={{ width: COLUNA_WIDTHS[index] }}>
                <strong>{coluna}</strong>
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {itens.map((item, index) => {
            const itemId = getTranscricaoItemId(item);
            const protocolo = getTranscricaoProtocolo(item);
            const status = getTranscricaoStatusFromItem(item);
            const { data, hora } = formatTranscricaoDataHora(getTranscricaoDataSolicitacao(item));

            return (
              <StyledTableRow
                key={String(itemId ?? `${protocolo}-${index}`)}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <StyledTableCell component="th" scope="row">
                  {protocolo}
                </StyledTableCell>
                <StyledTableCell align="left">
                  <span className="transcricao-lista-data">
                    <span>{data}</span>
                    {hora && <span className="transcricao-lista-hora">{hora}</span>}
                  </span>
                </StyledTableCell>
                <StyledTableCell align="left">
                  <TranscricaoSituacaoBadge status={status} />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Button
                    variant="text"
                    className="transcricao-lista-detalhes-button"
                    onClick={() => onVerDetalhes(item)}
                    aria-label={`Ver detalhes da transcrição ${protocolo}`}
                  >
                    <FontAwesomeIcon
                      icon={faEye as IconProp}
                      className="transcricao-lista-detalhes-icon"
                      aria-hidden="true"
                    />
                    <Typography
                      component="span"
                      style={accessibility.textStyleAccessibility}
                      className="transcricao-lista-detalhes-text"
                    >
                      DETALHES
                    </Typography>
                  </Button>
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TranscricaoListaTable;
